import type { Questionnaire, Task } from '@medplum/fhirtypes';

import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';

import { getPortalAppointmentTaskDescription } from '../../../lib/portalAppointmentTaskDisplay';
import { getTaskTypeCoding } from '../../tasks/services/taskFhirMapping';
import type { ClientPortalTaskActionType, ClientPortalTaskItem } from './clientPortalFhirTypes';
import {
  APPOINTMENT_REQUEST_TASK,
} from './appointmentRequestTask';
import {
  APPOINTMENT_RESCHEDULE_TASK,
} from './rescheduleRequestTask';
import {
  focusResourceType,
  isClinicalQuestionnaire,
  isClientFacingDocumentQuestionnaire,
} from './clientPortalClassifiers';

// ---------------------------------------------------------------------------
// Action type resolution
// ---------------------------------------------------------------------------

function resolveActionType(task: Task, questionnaire?: Questionnaire): ClientPortalTaskActionType {
  const focusType = focusResourceType(task);
  const codings = task.code?.coding ?? [];

  // Check all codes for specific action types via substring matching
  for (const coding of codings) {
    const code = coding.code?.toLowerCase() ?? '';
    if (code === 'client-document-completion') {
      return focusType === 'QuestionnaireResponse' ? 'document' : 'questionnaire';
    }
    if (code === 'rules-engine-form-assignment' || code === 'client-form-assignment' || code === 'client-assessment-assignment') {
      return focusType === 'Questionnaire' ? 'questionnaire' : 'document';
    }
  }

  // Check all codes for specific action types via substring matching
  for (const coding of codings) {
    const code = coding.code?.toLowerCase() ?? '';
    if (code.includes('appointment') || code.includes('schedule')) return 'appointment';
    if (code.includes('message')) return 'message';
    if (code.includes('billing') || code.includes('payment')) return 'billing';
    if (code.includes('service-request')) return 'service-request';
    if (code.includes('signature')) return 'signature';
  }

  switch (focusType) {
    case 'Questionnaire':
      return 'questionnaire';
    case 'QuestionnaireResponse':
      return 'document';
    case 'DocumentReference':
      return task.description?.toLowerCase().includes('sign') ? 'signature' : 'document';
    case 'ServiceRequest':
      return 'service-request';
    case 'Appointment':
      return 'appointment';
    default:
      break;
  }

  if (questionnaire) return 'questionnaire';

  return 'unknown';
}

function isPortalAppointmentPendingTask(task: Task): boolean {
  return (
    task.code?.coding?.some(
      (c) =>
        c.code === APPOINTMENT_REQUEST_TASK.code
        || c.code === APPOINTMENT_RESCHEDULE_TASK.code,
    ) ?? false
  );
}

function resolveHref(actionType: ClientPortalTaskActionType, task: Task): string {
  switch (actionType) {
    case 'questionnaire':
    case 'document':
    case 'signature':
      return `/client-portal/assessments/${task.id}`;
    case 'appointment':
      return '/client-portal';
    case 'billing':
      return '/client-portal/billing';
    case 'message':
      return '/client-portal/messages';
    case 'service-request':
      return FEATURE_VISIBILITY.clientPortalPendingServiceRequests
        ? '/client-portal/request-service'
        : '/client-portal';
    default:
      return '/client-portal';
  }
}

function resolveActionLabel(actionType: ClientPortalTaskActionType): string {
  switch (actionType) {
    case 'questionnaire':
      return 'Start';
    case 'document':
      return 'Open';
    case 'signature':
      return 'Sign';
    case 'message':
      return 'Open messages';
    case 'billing':
      return 'View billing';
    case 'appointment':
      return 'View';
    case 'service-request':
      return 'View request';
    default:
      return 'View';
  }
}

// ---------------------------------------------------------------------------
// Normalizer
// ---------------------------------------------------------------------------

export function normalizeClientTask(
  task: Task,
  questionnaire?: Questionnaire,
): ClientPortalTaskItem {
  const actionType = resolveActionType(task, questionnaire);
  const isClinical = isClinicalQuestionnaire(questionnaire);
  const isClientDoc = isClientFacingDocumentQuestionnaire(questionnaire);

  const focusRef = task.focus?.reference;
  const hasMalformedFocus =
    actionType === 'questionnaire' && focusRef && !questionnaire;

  const portalTitle = getPortalAppointmentTaskDescription(task, 'client');
  const showActionButton = !isPortalAppointmentPendingTask(task);

  return {
    id: task.id ?? '',
    title:
      questionnaire?.title ??
      portalTitle ??
      task.description ??
      getTaskTypeCoding(task)?.display ??
      task.focus?.display ??
      'Task',
    description: task.description && questionnaire?.title ? task.description : undefined,
    status: task.status,
    priority: task.priority,
    due: task.restriction?.period?.end ?? undefined,
    authoredOn: task.authoredOn ?? undefined,
    actionType: hasMalformedFocus ? 'unknown' : actionType,
    actionLabel: hasMalformedFocus ? 'Unavailable' : resolveActionLabel(actionType),
    showActionButton: hasMalformedFocus ? false : showActionButton,
    href: hasMalformedFocus ? '/client-portal' : resolveHref(actionType, task),
    focusRef,
    questionnaireRef: questionnaire ? `Questionnaire/${questionnaire.id}` : undefined,
    questionnaireTitle: questionnaire?.title,
    isClinicalAssessment: isClinical,
    isClientFacingDocument: isClientDoc,
  };
}
